import './DashboardPages.css';
import DashboardLayout from '../components/DashboardLayout';
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { organizationsApi } from '../api/organizations';
import { ministriesApi } from '../api/ministries';
import { useAuth } from '../context/AuthContext';
import defaultUserPhoto from '../assets/Assemblie_DefaultUserIcon.png';

function formatPhoneNumber(phoneNumber) {
	// Remove all non-numeric characters
	const cleaned = phoneNumber?.replace(/\D/g, '');
	// Check if we have a valid number
	if (!cleaned || cleaned.length !== 10) return phoneNumber;
	// Format as (XXX) XXX-XXXX
	return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
		6
	)}`;
}

function MinistryDetail() {
	const [ministry, setMinistry] = useState(null);
	const [team, setTeam] = useState(null);
	const [checkins, setCheckins] = useState([]);
	const { ministryId } = useParams();
	const { orgData } = useAuth();

	useEffect(() => {
		const fetchMinistryDetails = async () => {
			try {
				const ministryData = await ministriesApi.getOne(
					orgData.id,
					ministryId
				);
				setMinistry(ministryData.ministry);

				// Move check-ins after team fetch to avoid blocking
				if (ministryData.ministry.teamId) {
					try {
						const teamData = await organizationsApi.teams.getOne(
							orgData.id,
							ministryData.ministry.teamId
						);
						setTeam(teamData.team);
					} catch (teamError) {
						console.error('Error fetching team:', teamError);
					}
				}

				// Try fetching check-ins, but don't let it block other data
				try {
					const checkinsData = await ministriesApi.checkins.getAll(
						orgData.id,
						ministryId
					);

					setCheckins(checkinsData.checkins || []);
				} catch (checkinError) {
					console.error('Error fetching check-ins:', checkinError);
					setCheckins([]); // Set empty array on error
				}
			} catch (error) {
				console.error('Error fetching ministry details:', error);
			}
		};

		if (orgData?.id && ministryId) {
			fetchMinistryDetails();
		}
	}, [ministryId, orgData]);

	if (!ministry) return <div>Loading...</div>;

	const activeTeamMembers =
		ministry.Team?.Users?.filter((user) => user.TeamUser.isActive) || [];

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				<h1>{ministry.name}</h1>
				<div className='content-section'>
					<div className='detail-card'>
						<h2>Description</h2>
						<p>{ministry.description}</p>
					</div>

					<div className='detail-card'>
						<h2>Current Check-ins</h2>
						{checkins.length === 0 ? (
							<p>No users currently checked in.</p>
						) : (
							<div className='table-responsive'>
								<table className='users-table'>
									<thead>
										<tr>
											<th>Photo</th>
											<th>Name</th>
											<th>Email</th>
											<th>Phone Number</th>
											<th>Check-in Time</th>
										</tr>
									</thead>
									<tbody>
										{checkins.map((checkin) => (
											<tr
												key={
													checkin.FamilyMember
														? checkin.FamilyMember
																.id
														: checkin.userId
												}>
												<td>
													<img
														src={
															checkin.FamilyMember
																? checkin
																		.FamilyMember
																		.userPhoto ||
																  defaultUserPhoto
																: checkin.User
																		.userPhoto ||
																  defaultUserPhoto
														}
														alt={`${
															checkin.FamilyMember
																? checkin
																		.FamilyMember
																		.firstName
																: checkin.User
																		.firstName
														} ${
															checkin.FamilyMember
																? checkin
																		.FamilyMember
																		.lastName
																: checkin.User
																		.lastName
														}`}
														className='user-avatar'
													/>
												</td>
												<td className='name-container'>
													<span className='user-name'>
														{checkin.FamilyMember
															? checkin
																	.FamilyMember
																	.firstName
															: checkin.User
																	.firstName}
													</span>
													<span className='user-name'>
														{checkin.FamilyMember
															? checkin
																	.FamilyMember
																	.lastName
															: checkin.User
																	.lastName}
													</span>
												</td>
												<td className='user-email'>
													{checkin.FamilyMember
														? ''
														: checkin.User.email}
												</td>
												<td className='user-phone'>
													{checkin.FamilyMember
														? checkin.FamilyMember
																.creator
																.phoneNumber
														: checkin.User
																.phoneNumber}
												</td>
												<td>
													{new Date(
														checkin.updatedAt
													).toLocaleString()}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>

					{activeTeamMembers.length > 0 && (
						<div className='detail-card'>
							<h2>Active Team Members</h2>
							<div className='members-grid'>
								{activeTeamMembers.map((member) => (
									<div
										key={member.id}
										className='member-card'
										style={{
											'--primary-color':
												orgData?.primaryColor ||
												'#4b95a3',
											'--primary-color-light': `${
												orgData?.primaryColor ||
												'#4b95a3'
											}1A`,
										}}>
										<div className='member-details'>
											<h3>{`${member.firstName} ${member.lastName}`}</h3>
											<p>{member.email}</p>
											{member.phoneNumber && (
												<p>{member.phoneNumber}</p>
											)}
											<p className='access-level'>
												{member.accessLevel}
											</p>
											{member.TeamUser.isTeamLead && (
												<p className='team-lead-badge'>
													Team Lead
												</p>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					{team && (
						<div
							className='dashboard-card'
							style={{
								'--primary-color':
									orgData?.primaryColor || '#4b95a3',
								'--primary-color-light': `${
									orgData?.primaryColor || '#4b95a3'
								}1A`,
								marginTop: '24px',
							}}>
							<Link
								to={`/dashboard/teams/${team.id}`}
								style={{
									textDecoration: 'none',
									color: 'inherit',
								}}>
								<span className='material-icons card-icon'>
									groups
								</span>
								<h3>{team.name}</h3>
							</Link>
						</div>
					)}
				</div>
			</div>
		</DashboardLayout>
	);
}

export default MinistryDetail;
